import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import Loading from "../Loading/Loading";
import { plog } from "../../utils/perfLog";

/**
 * Only media the browser is actually fetching right now should hold the
 * loader open.
 *
 * - `loading="lazy"` images below the fold stay `complete === false` until the
 *   user scrolls them into view, so counting them means the gate can never
 *   reach zero and always burns its full timeout.
 * - Videos are excluded outright: the hero video is multi-megabyte decorative
 *   background that streams progressively, and the page is perfectly usable
 *   while it buffers.
 */
function countPendingMedia(root) {
  if (!root) return 0;
  let n = 0;
  root.querySelectorAll("img").forEach((img) => {
    if (img.loading === "lazy") return;
    if (!img.complete) n += 1;
  });
  return n;
}

const NavigationResourceLoader = () => {
  const { pathname } = useLocation();
  const [prevPath, setPrevPath] = useState(pathname);
  const [show, setShow] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const isInitialMount = useRef(true);
  const startedRef = useRef(Date.now());
  const stableRef = useRef(0);
  const exitTimeoutRef = useRef(null);

  // Synchronous state adjustment during render when route changes.
  // The overlay mounts with instant 100% opacity on frame 0, preventing any background route flash.
  if (pathname !== prevPath) {
    setPrevPath(pathname);
    setShow(true);
    setIsExiting(false);
    startedRef.current = Date.now();
    stableRef.current = 0;
    if (exitTimeoutRef.current) {
      clearTimeout(exitTimeoutRef.current);
    }
  }

  useEffect(() => {
    if (!show || isExiting) return undefined;

    const root = document.getElementById("root");
    if (!root) {
      setShow(false);
      setIsExiting(false);
      return undefined;
    }

    const isInitial = isInitialMount.current;
    const graceMs = isInitial ? 250 : 100;
    const minMs = isInitial ? 600 : 300;
    const pollMs = 40;
    const needStableTicks = 2;
    const maxMs = isInitial ? 1500 : 700;

    let cancelled = false;
    let intervalId;

    const tick = () => {
      if (cancelled) return;
      const elapsed = Date.now() - startedRef.current;
      const pending = countPendingMedia(root);

      if (elapsed < graceMs) {
        return;
      }

      if (pending === 0) {
        stableRef.current += 1;
      } else {
        stableRef.current = 0;
      }

      const stableEnough =
        stableRef.current >= needStableTicks && elapsed >= minMs;
      const timedOut = elapsed >= maxMs;

      if (stableEnough || timedOut) {
        plog(
          "gate",
          `NavResourceLoader: GATE RELEASED for ${pathname} after ${elapsed}ms ` +
            `(${timedOut ? "TIMED OUT" : "media settled"}, pending=${pending})`
        );
        clearInterval(intervalId);
        if (!cancelled) {
          isInitialMount.current = false;
          setIsExiting(true);
          exitTimeoutRef.current = setTimeout(() => {
            if (!cancelled) {
              setShow(false);
              setIsExiting(false);
            }
          }, 400);
        }
      }
    };

    intervalId = setInterval(tick, pollMs);
    tick();

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      if (exitTimeoutRef.current) {
        clearTimeout(exitTimeoutRef.current);
      }
    };
  }, [pathname, show, isExiting]);

  if (!show) return null;
  return <Loading isExiting={isExiting} />;
};

export default NavigationResourceLoader;
